import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Leads from './pages/Leads';
import LeadDetail from './pages/LeadDetail';
import Campaigns from './pages/Campaigns';
import Calendar from './pages/Calendar';
import Users from './pages/Users';
import ImportLeads from './pages/ImportLeads';
import Security from './pages/Security';
import CallbackNotifier from './components/CallbackNotifier';
import { Toaster } from './components/ui/sonner';
import { LanguageProvider } from './context/LanguageContext';
import { setupAxiosInterceptors } from './lib/axiosSetup';
import './App.css';

setupAxiosInterceptors();

function App() {
  // Initialize synchronously from localStorage so deep-links (e.g. /users, /security) don't
  // flicker-redirect to '/' before the effect runs.
  const [user, setUser] = useState(() => {
    try {
      const token = localStorage.getItem('token');
      const userData = localStorage.getItem('user');
      return token && userData ? JSON.parse(userData) : null;
    } catch {
      return null;
    }
  });
  const [theme, setTheme] = useState(() => localStorage.getItem('theme') || 'light');

  useEffect(() => {
    document.documentElement.classList.toggle('dark', theme === 'dark');
  }, [theme]);

  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
    localStorage.setItem('theme', newTheme);
    document.documentElement.classList.toggle('dark', newTheme === 'dark');
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  };

  return (
    <div className="App">
      <LanguageProvider>
        <BrowserRouter>
        {user && <CallbackNotifier user={user} />}
        <Routes>
          <Route
            path="/login"
            element={!user ? <Login setUser={setUser} /> : <Navigate to="/" />}
          />
          <Route
            path="/"
            element={
              user ? (
                <Dashboard user={user} onLogout={handleLogout} theme={theme} onToggleTheme={toggleTheme} />
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/leads"
            element={
              user ? (
                <Leads user={user} onLogout={handleLogout} theme={theme} onToggleTheme={toggleTheme} />
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/leads/:leadId"
            element={
              user ? (
                <LeadDetail user={user} onLogout={handleLogout} theme={theme} onToggleTheme={toggleTheme} />
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/campaigns"
            element={
              user ? (
                <Campaigns user={user} onLogout={handleLogout} theme={theme} onToggleTheme={toggleTheme} />
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/calendar"
            element={
              user ? (
                <Calendar user={user} onLogout={handleLogout} theme={theme} onToggleTheme={toggleTheme} />
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/users"
            element={
              user && user.role === 'admin' ? (
                <Users user={user} onLogout={handleLogout} theme={theme} onToggleTheme={toggleTheme} />
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            path="/import"
            element={
              user && user.role === 'admin' ? (
                <ImportLeads user={user} onLogout={handleLogout} theme={theme} onToggleTheme={toggleTheme} />
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            path="/security"
            element={
              user ? (
                <Security user={user} onLogout={handleLogout} theme={theme} onToggleTheme={toggleTheme} />
              ) : (
                <Navigate to="/login" />
              )
            }
          />
        </Routes>
      </BrowserRouter>
      </LanguageProvider>
      <Toaster />
    </div>
  );
}

export default App;
